import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  SvgIcon,
  Tooltip,
} from "@mui/material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import "./OrderItemCard.scss";
import { ExpandMore } from "@mui/icons-material";
import { ReactComponent as TrashIcon } from "../../../assets/icons/trash-icon.svg";
import { ReactComponent as MinusIcon } from "../../../assets/icons/minus-icon.svg";
import { useNavigate } from "react-router-dom";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { ReactComponent as NoImgIcon } from "../../../assets/icons/no-img-icon.svg";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

type CartItemCardProps = {
  item: any;
  orderId: number;
  onDeleteOrderItem?: (partnerId: number) => void;
  onDeleteService?: (partnerId: number, serviceId: number) => void;
  onDeleteSubService?: (orderId: number, serviceId: number) => void;
};

const OrderItemCard: React.FC<CartItemCardProps> = ({
  item,
  orderId,
  onDeleteService,
  onDeleteSubService,
}) => {
  const navigate = useNavigate();

  //  detect planner item
  const isPlanner = !!item?.plannerId || Array.isArray(item?.packages);

  //  images: partnerImages OR plannerImages
  const images = Array.isArray(item?.partnerImages)
    ? item.partnerImages
    : Array.isArray(item?.plannerImages)
      ? item.plannerImages
      : [];
  const partnerImage = images?.[0]?.url || false;

  //  title fields (partner vs planner)
  const displayName = item?.partnerName ?? item?.plannerName ?? "-";
  const displayLocation = item?.partnerLocation ?? item?.plannerLocation ?? "-";
  const displayDesc = item?.partnerDescription ?? item?.plannerDescription ?? "";
  const displayRating =
    (typeof item?.partnerAvgRating === "number" && item.partnerAvgRating) ||
    (typeof item?.plannerAvgRating === "number" && item.plannerAvgRating) ||
    4.0;

  //  provider services
  const services = Array.isArray(item?.services) ? item.services : [];

  //  planner packages
  const packages = Array.isArray(item?.packages) ? item.packages : [];

  //  SAFE: avoid navigating if partnerId missing (planner has no detail page)
  const handlePartnerClick = () => {
    if (!item?.partnerId) return;
    navigate(`/home/list-of-funeral-homes/details/${item.partnerId}`);
  };

  //  totals
  const computedServicesTotal = services.reduce((sum: number, service: any) => {
    const subs = Array.isArray(service?.subServices) ? service.subServices : [];
    return (
      sum +
      subs.reduce(
        (s: number, sub: any) => s + parseFloat(sub.price),
        0
      )
    );
  }, 0);

  const computedPackagesTotal = packages.reduce((sum: number, p: any) => {
    const price = Number(p?.price ?? 0);
    return sum + (Number.isFinite(price) ? price : 0);
  }, 0);

  const fallbackRaw =
    item?.plannerTotalPrice ??
    item?.partnerTotalPrice ??
    item?.totalAmount ??
    item?.summary?.grandTotal ??
    item?.summary?.totalAmount ??
    item?.orderTotal ??
    item?.totalPrice ??
    0;

  const fallbackTotal =
    Number(fallbackRaw) || parseFloat(String(fallbackRaw)) || 0;

  const finalTotal =
    computedServicesTotal > 0
      ? computedServicesTotal
      : computedPackagesTotal > 0
        ? computedPackagesTotal
        : fallbackTotal;

  return (
    <Box
      className={`order-item-card__wrapper ${isPlanner ? "order-item-card__wrapper--planner" : ""
        }`}
    >
      {/* ======= MAIN CARD (top row) ======= */}
      <Box className="order-item-card">
        {/* Left: Image */}
        <Box className="order-item-card__image">
          {partnerImage ? (
            <img src={partnerImage} alt={displayName} />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "0.5rem",
                color: "#555",
                backgroundColor: "#F2F5F9",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              <SvgIcon
                component={NoImgIcon}
                sx={{ height: "40px", width: "40px", color: "#A0AEC0" }}
                inheritViewBox
              />
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#4A5568" }}>
                No Image Available
              </Typography>
            </Box>
          )}

          <Box className="order-item-card__rating">
            <StarRoundedIcon className="order-item-card__rating-icon" />
            <Typography className="order-item-card__rating-value">
              {Number(displayRating).toFixed(1)}
            </Typography>
          </Box>
        </Box>

        {/* Right: header + desc */}
        <Box className="order-item-card__info">
          <Box className="order-item-card__info-header">
            <Box className="order-item-card__name-location">
              <Typography
                className="order-item-card__partner-name"
                onClick={handlePartnerClick}
                sx={{ cursor: item?.partnerId ? "pointer" : "default" }}
              >
                {displayName}
              </Typography>

              <Box
                component="span"
                sx={{
                  color: "#104145",
                  mx: 0.5,
                  fontWeight: 900,
                  fontSize: "18px",
                  lineHeight: 1,
                }}
              >
                &bull;
              </Box>

              <Typography className="order-item-card__partner-location">
                {displayLocation}
              </Typography>
            </Box>
          </Box>

          {displayDesc ? (
            <Typography className="order-item-card__partner-desc">
              {displayDesc}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {/* ======= BOTTOM SECTION ======= */}
      <Box className="order-item-card__accordion-section">
        {/*  PLANNER VIEW (full-width, starts from left under image like screenshot) */}
        {isPlanner ? (
          <Box className="order-item-card__planner-section">
            {packages.map((pkg: any) => {
              const included = Array.isArray(pkg?.servicesIncluded)
                ? pkg.servicesIncluded
                : [];

              return (
                <Box
                  key={pkg?.orderItemId ?? pkg?.packageId ?? pkg?.packageName}
                  className="order-item-card__planner-package"
                >
                  <Typography className="order-item-card__planner-package-name">
                    {pkg?.packageName ?? "Package"}
                  </Typography>

                  {pkg?.description ? (
                    <Typography className="order-item-card__planner-package-desc">
                      {pkg.description}
                    </Typography>
                  ) : null}

                  <Divider className="order-item-card__planner-divider" />

                  <Typography className="order-item-card__planner-services-title">
                    Services Included
                  </Typography>

                  <Box className="order-item-card__planner-services">
                    {included.map((s: any, idx: number) => (
                      <Box
                        key={`${pkg?.packageId}-${s?.serviceId ?? idx}`}
                        className="order-item-card__planner-service-pill"
                      >
                        <CheckRoundedIcon className="order-item-card__planner-check" />
                        <Typography className="order-item-card__planner-service-name">
                          {s?.serviceName ?? "-"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <>
            {/*  PROVIDER VIEW (existing accordion) */}
            {services.map((service: any) => (
              <Accordion
                key={service.serviceId}
                className="order-item-card__accordion"
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  className="order-item-card__accordion-summary"
                >
                  <Typography
                    className="order-item-card__service-name"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    {service.serviceName}
                    {service.needType && (
                      <>
                        <Box
                          component="span"
                          sx={{
                            color: "#104145",
                            mx: 1,
                            fontWeight: 900,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          &bull;
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color: "#666",
                            fontSize: "14px",
                            fontWeight: 400,
                          }}
                        >
                          {service.needType === "pre_need" ? "Preneed" : "Immediate Need"}
                        </Box>
                      </>
                    )}
                  </Typography>

                  <Box sx={{ marginLeft: "auto", display: "flex" }}>
                    <IconButton
                      className="order-item-card__service-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onDeleteService) {
                          onDeleteService(orderId, service.serviceId);
                        }
                      }}
                    >
                      <TrashIcon />
                    </IconButton>
                  </Box>
                </AccordionSummary>

                <Typography className="order-item-card__service-title">
                  Summary of Steps -{" "}
                  <span style={{ color: "#9D3535" }}>Customized.</span>
                </Typography>

                <AccordionDetails className="order-item-card__accordion-details">
                  {(Array.isArray(service?.subServices)
                    ? service.subServices
                    : []
                  ).map((sub: any) => (
                    <Box
                      key={`${service.serviceId}-${sub.subServiceId}`}
                      className="order-item-card__subservice"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onDeleteSubService) {
                            onDeleteSubService(orderId, sub.orderItemId);
                          }
                        }}
                      >
                        <MinusIcon className="order-item-card__minus-service" />

                        <Typography className="order-item-card__subservice-name">
                          {sub.subServiceName}
                        </Typography>
                      </Box>

                      <Box
                        className="order-item-card__subservice-actions"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        {sub.itemType === "merchandise" && (sub.quantity || 1) > 0 && (
                          <Typography sx={{ color: "#104145", fontWeight: 700, fontSize: "14px" }}>
                            Quantity: {sub.quantity}
                          </Typography>
                        )}
                        <Typography className="order-item-card__subservice-price">
                          $ {parseFloat(sub.price)}
                        </Typography>

                      </Box>
                    </Box>
                  ))}
                </AccordionDetails>

                <Divider />
              </Accordion>
            ))}
          </>
        )}

        {/* ======= TOTAL PRICE BAR (Both) ======= */}
        <Box className="order-item-card__total-price-bar">
          <Typography className="order-item-card__total-price-label">
            Total Price
          </Typography>
          <Typography className="order-item-card__total-price-value">
            $ {finalTotal.toFixed(0)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default OrderItemCard;
